import { X } from 'lucide-react'
import { useState, useEffect } from 'react'

import { DateQuickSelect } from '@/components/ui/date-quick-select'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useMilestone,
  useCreateMilestone,
  useUpdateMilestone,
  type MilestoneStatus,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface MilestoneFormProps {
  projectId: string
  milestoneId?: string | null
  onClose: () => void
}

/**
 * 里程碑表单组件
 */
export function MilestoneForm({ projectId, milestoneId, onClose }: MilestoneFormProps) {
  const isEditing = !!milestoneId
  const { data: milestone, isLoading: isLoadingMilestone } = useMilestone(milestoneId || '')
  const createMutation = useCreateMilestone()
  const updateMutation = useUpdateMilestone()

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    dueDate: '',
    status: 'open' as MilestoneStatus,
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  // 加载编辑数据
  useEffect(() => {
    if (milestone) {
      setFormData({
        name: milestone.name,
        description: milestone.description || '',
        dueDate: milestone.dueDate || '',
        status: milestone.status,
      })
    }
  }, [milestone])

  const validate = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = '请输入里程碑名称'
    } else if (formData.name.length > 100) {
      newErrors.name = '名称不能超过100个字符'
    }

    if (formData.description.length > 2000) {
      newErrors.description = '描述不能超过2000个字符'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    const data = {
      name: formData.name.trim(),
      description: formData.description.trim() || undefined,
      dueDate: formData.dueDate || undefined,
      status: isEditing ? formData.status : undefined,
    }

    if (isEditing && milestoneId) {
      updateMutation.mutate(
        { id: milestoneId, data },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )
    } else {
      createMutation.mutate(
        { projectId, data },
        {
          onSuccess: () => {
            onClose()
          },
        }
      )
    }
  }

  const isPending = createMutation.isPending || updateMutation.isPending

  if (isEditing && isLoadingMilestone) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="rounded-lg bg-background p-6">
          <div className="text-muted-foreground">加载中...</div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="w-full max-w-md rounded-lg bg-background shadow-lg">
        {/* 头部 */}
        <div className="flex items-center justify-between border-b px-6 py-4">
          <h2 className="text-lg font-semibold">
            {isEditing ? '编辑里程碑' : '新建里程碑'}
          </h2>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* 表单 */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* 名称 */}
            <div>
              <label className="mb-2 block text-sm font-medium">
                名称 <span className="text-destructive">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                placeholder="例如：版本 1.0"
                maxLength={100}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm',
                  errors.name && 'border-destructive'
                )}
              />
              {errors.name && (
                <p className="mt-1 text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* 描述 */}
            <div>
              <label className="mb-2 block text-sm font-medium">描述</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder="里程碑描述..."
                rows={3}
                maxLength={2000}
                className={cn(
                  'w-full rounded-md border bg-background px-3 py-2 text-sm resize-none',
                  errors.description && 'border-destructive'
                )}
              />
              {errors.description && (
                <p className="mt-1 text-sm text-destructive">{errors.description}</p>
              )}
              <p className="mt-1 text-xs text-muted-foreground">
                {formData.description.length}/2000
              </p>
            </div>

            {/* 截止日期 */}
            <div>
              <label className="mb-2 block text-sm font-medium">截止日期</label>
              <input
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
              {/* 日期快捷选项 (Requirements: 11.6) */}
              <DateQuickSelect
                value={formData.dueDate}
                onChange={(date) => setFormData((prev) => ({ ...prev, dueDate: date }))}
                className="mt-2"
                options={{
                  includeToday: false,
                  includeTomorrow: true,
                  includeNextMonday: true,
                  includeThisWeekEnd: true,
                }}
              />
            </div>

            {/* 状态（仅编辑时显示） */}
            {isEditing && (
              <div>
                <label className="mb-2 block text-sm font-medium">状态</label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData((prev) => ({ ...prev, status: value as MilestoneStatus }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="选择状态" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">进行中</SelectItem>
                    <SelectItem value="closed">已完成</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          {/* 错误提示 */}
          {(createMutation.error || updateMutation.error) && (
            <div className="mt-4 rounded-md bg-destructive/10 p-3 text-sm text-destructive">
              {(createMutation.error || updateMutation.error)?.message || '操作失败'}
            </div>
          )}

          {/* 按钮 */}
          <div className="mt-6 flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted"
            >
              取消
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {isPending ? '保存中...' : isEditing ? '保存' : '创建'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
